import * as React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { navigate } from 'wouter/use-location';
import { useQueryClient } from '@tanstack/react-query';
import { useRecoilValue } from 'recoil';
import { ICmdTableCustomerCell, ICmdTableUserCell, TSupplierRecords } from './interfaces';
import {
  SUPPLIER_ACTIVE_KEY,
  SUPPLIER_ADDRESS_KEY,
  SUPPLIER_CREATED_AT_KEY,
  SUPPLIER_CREATED_BY_KEY,
  SUPPLIER_CUSTOMER_ID_KEY,
  SUPPLIER_DELETED_AT_KEY,
  SUPPLIER_DETAIL_KEY,
  SUPPLIER_EMAIL_KEY,
  SUPPLIER_MODIFIED_AT_KEY,
  SUPPLIER_MODIFIED_BY_KEY,
  SUPPLIER_NAME_KEY,
  SUPPLIER_NOTE_KEY,
  SUPPLIER_PHONE_KEY,
  SUPPLIER_QUERY_KEY,
  SUPPLIER_TABLE_KEY,
  SUPPLIER_TYPE_KEY,
} from './constants';
import { TABLE_SUPPLIER } from '../../../Services/TableService';
import { URL_SUPPLIERS } from '../../../router/constants';
// import * as CmdTableState from '../../CmdTable/CmdTableState';
import ThSearch from '../../CmdTable/TableHeadCell/ThSearch';
import { ICmdTableColumn, ICmdTableFilter } from '../../CmdTable/types';
import CustomerCell from '../../CmdTable/TableCells/CustomerCell';
import IconCell from '../../CmdTable/TableCells/IconCell';
import DateTimeCell from '../../CmdTable/TableCells/DateTimeCell';
import ThSimpleSelect from '../../CmdTable/TableHeadCell/ThSimpleSelect';
import ThMultiSelect from '../../CmdTable/TableHeadCell/ThMultiSelect';
import ThDateTime from '../../CmdTable/TableHeadCell/ThDateTime';
import TextCell from '../../CmdTable/TableCells/TextCell';
import DetailAction from '../../CmdTable/TableActions/DetailAction';
import DeleteAction from '../../CmdTable/TableActions/DeleteAction';
// import * as HttpService from '../../../Services/HttpService';
import {
  columnsAtom,
  filterForRequestAtomFamily,
  sortFilterAtomFamily,
} from '../../CmdTable/CmdTableState';
import { putRequest } from '../../../Services/HttpService';
import useAnalytics from '../../../hooks/useAnalytics';

interface IUseParkingTag {
  columns: ColumnDef<TSupplierRecords>[];
  columnOrder: string[];
  columnVisibility: { [key: string]: boolean };
}

export default function useSuppliers(): IUseParkingTag {
  const { formatMessage: f } = useIntl();
  const queryClient = useQueryClient();
  // const tableFiltersKey = useRecoilValue(CmdTableState.tableFilters);
  const filterForRequest = useRecoilValue<ICmdTableFilter>(
    filterForRequestAtomFamily(SUPPLIER_TABLE_KEY)
  );
  const sortFilter = useRecoilValue<{ [key: string]: string }>(
    sortFilterAtomFamily(SUPPLIER_TABLE_KEY)
  );
  const columnsTable = useRecoilValue<ICmdTableColumn[]>(columnsAtom);
  const { trackEvent } = useAnalytics();

  const updateTable = React.useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [SUPPLIER_TABLE_KEY, filterForRequest, sortFilter],
    });
  }, [queryClient, filterForRequest, sortFilter]);

  const handleRemove = React.useCallback(
    async (id: number, customerId?: number | number[]) => {
      trackEvent(['supplier table', 'click', 'Action delete']);
      if (customerId && !Array.isArray(customerId)) {
        const response = await putRequest(
          `/v1/${TABLE_SUPPLIER}/${id}/remove?customerId=${customerId}`,
          { id }
        );
        if (response) {
          updateTable();
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateTable]
  );

  const handleAction = (id: number, customerId?: number | number[]) => {
    if (customerId && !Array.isArray(customerId)) {
      navigate(`${URL_SUPPLIERS}/edit/${id}?customerId=${customerId}`);
      trackEvent(['supplier table', 'click', 'Action edit']);
    }
  };

  const columns = React.useMemo<ColumnDef<TSupplierRecords>[]>(
    () => [
      {
        accessorKey: SUPPLIER_NAME_KEY,
        header: () => (
          <ThSearch
            tableName={SUPPLIER_TABLE_KEY}
            id={SUPPLIER_NAME_KEY}
            title={f({ id: `table_list.columns.${SUPPLIER_QUERY_KEY}.name` })}
            // tooltip={f({ id: `table_list.columns.parkingTags.${PARKING_SUPPLIER_NAME_KEY}.tooltip` })}
            isSortable={true}
            // minWidth="176px"
            // maxWidth="300px"
          />
        ),
        cell: (info) =>
          info.row.original?.[SUPPLIER_NAME_KEY] ? (
            <TextCell
              text={info.getValue<string>()}
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: SUPPLIER_CUSTOMER_ID_KEY,
        header: () => (
          <ThSimpleSelect
            tableName={SUPPLIER_TABLE_KEY}
            id={SUPPLIER_CUSTOMER_ID_KEY}
            title={f({ id: `table_list.columns.${SUPPLIER_CUSTOMER_ID_KEY}` })}
            // tooltip={f({ id: `table_list.columns.${PARKING_SUPPLIER_CUSTOMER_ID_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.[SUPPLIER_CUSTOMER_ID_KEY] ? (
            <CustomerCell customer={info.getValue<ICmdTableCustomerCell>()} />
          ) : (
            <TextCell text="" />
          ),
        // size: 260,
        minSize: 260,
      },
      {
        accessorKey: SUPPLIER_ACTIVE_KEY,
        header: () => (
          <ThSimpleSelect
            tableName={SUPPLIER_TABLE_KEY}
            id={SUPPLIER_ACTIVE_KEY}
            title={f({ id: `table_list.columns.${SUPPLIER_QUERY_KEY}.${SUPPLIER_ACTIVE_KEY}` })}
            // tooltip={f({ id: `table_list.columns.parkingTags.${PARKING_SUPPLIER_ACTIVE_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.[SUPPLIER_ACTIVE_KEY] ? (
            <IconCell isIcon={info.getValue<boolean>()} icon="cmd-icon-active text-success" />
          ) : (
            <TextCell text="" />
          ),
        size: 100,
      },
      {
        accessorKey: SUPPLIER_TYPE_KEY,
        header: () => (
          <ThMultiSelect
            tableName={SUPPLIER_TABLE_KEY}
            id={SUPPLIER_TYPE_KEY}
            title={f({ id: `table_list.columns.${SUPPLIER_QUERY_KEY}.${SUPPLIER_TYPE_KEY}` })}
            // tooltip={f({ id: `table_list.columns.${PARKING_SUPPLIER_MODIFIED_BY_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.[SUPPLIER_TYPE_KEY] ? (
            <TextCell
              text={info.getValue<string>()}
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: SUPPLIER_ADDRESS_KEY,
        header: () => (
          <ThSearch
            tableName={SUPPLIER_TABLE_KEY}
            id={SUPPLIER_ADDRESS_KEY}
            title={f({ id: `table_list.columns.${SUPPLIER_QUERY_KEY}.${SUPPLIER_ADDRESS_KEY}` })}
            // tooltip={f({ id: `table_list.columns.parkingTags.${PARKING_SUPPLIER_NAME_KEY}.tooltip` })}
            isSortable={true}
            // minWidth="176px"
            // maxWidth="300px"
          />
        ),
        cell: (info) =>
          info.row.original?.[SUPPLIER_ADDRESS_KEY] ? (
            <TextCell
              text={info.getValue<string>()}
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: SUPPLIER_EMAIL_KEY,
        header: () => (
          <ThSearch
            tableName={SUPPLIER_TABLE_KEY}
            id={SUPPLIER_EMAIL_KEY}
            title={f({ id: `table_list.columns.${SUPPLIER_QUERY_KEY}.${SUPPLIER_EMAIL_KEY}` })}
            // tooltip={f({ id: `table_list.columns.parkingTags.${PARKING_SUPPLIER_NAME_KEY}.tooltip` })}
            isSortable={true}
            // minWidth="176px"
            // maxWidth="300px"
          />
        ),
        cell: (info) =>
          info.row.original?.[SUPPLIER_EMAIL_KEY] ? (
            <TextCell
              text={info.getValue<string>()}
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: SUPPLIER_PHONE_KEY,
        header: () => (
          <ThSearch
            tableName={SUPPLIER_TABLE_KEY}
            id={SUPPLIER_PHONE_KEY}
            title={f({ id: `table_list.columns.${SUPPLIER_QUERY_KEY}.${SUPPLIER_PHONE_KEY}` })}
            // tooltip={f({ id: `table_list.columns.parkingTags.${PARKING_SUPPLIER_NAME_KEY}.tooltip` })}
            isSortable={true}
            // minWidth="176px"
            // maxWidth="300px"
          />
        ),
        cell: (info) =>
          info.row.original?.[SUPPLIER_PHONE_KEY] ? (
            <TextCell
              text={info.getValue<string>()}
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: SUPPLIER_NOTE_KEY,
        header: () => (
          <ThSearch
            tableName={SUPPLIER_TABLE_KEY}
            id={SUPPLIER_NOTE_KEY}
            title={f({ id: `table_list.columns.${SUPPLIER_QUERY_KEY}.${SUPPLIER_NOTE_KEY}` })}
            // tooltip={f({ id: `table_list.columns.parkingTags.${PARKING_SUPPLIER_NAME_KEY}.tooltip` })}
            isSortable={true}
            // minWidth="176px"
            // maxWidth="300px"
          />
        ),
        cell: (info) =>
          info.row.original?.[SUPPLIER_NOTE_KEY] ? (
            <TextCell
              text={info.getValue<string>()}
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: SUPPLIER_CREATED_BY_KEY,
        header: () => (
          <ThMultiSelect
            tableName={SUPPLIER_TABLE_KEY}
            id={SUPPLIER_CREATED_BY_KEY}
            title={f({ id: `table_list.columns.${SUPPLIER_CREATED_BY_KEY}` })}
            // tooltip={f({ id: `table_list.columns.${PARKING_SUPPLIER_CREATED_BY_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.createdBy ? (
            <TextCell text={info.getValue<ICmdTableUserCell>().name || ''} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: SUPPLIER_CREATED_AT_KEY,
        header: () => (
          <ThDateTime
            tableName={SUPPLIER_TABLE_KEY}
            id={SUPPLIER_CREATED_AT_KEY}
            title={f({ id: `table_list.columns.${SUPPLIER_CREATED_AT_KEY}` })}
            // tooltip={f({ id: `table_list.columns.${PARKING_SUPPLIER_CREATED_AT_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.createdAt ? (
            <DateTimeCell datetime={info.getValue<string | null>()} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: SUPPLIER_MODIFIED_BY_KEY,
        header: () => (
          <ThMultiSelect
            tableName={SUPPLIER_TABLE_KEY}
            id={SUPPLIER_MODIFIED_BY_KEY}
            title={f({ id: `table_list.columns.${SUPPLIER_MODIFIED_BY_KEY}` })}
            // tooltip={f({ id: `table_list.columns.${PARKING_SUPPLIER_MODIFIED_BY_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.modifiedBy ? (
            <TextCell text={info.getValue<ICmdTableUserCell>().name || ''} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: SUPPLIER_MODIFIED_AT_KEY,
        header: () => (
          <ThDateTime
            id={SUPPLIER_MODIFIED_AT_KEY}
            tableName={SUPPLIER_TABLE_KEY}
            title={f({ id: `table_list.columns.${SUPPLIER_MODIFIED_AT_KEY}` })}
            // tooltip={f({ id: `table_list.columns.${PARKING_SUPPLIER_MODIFIED_AT_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.modifiedBy ? (
            <DateTimeCell datetime={info.getValue<string | null>()} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },

      {
        accessorKey: SUPPLIER_DETAIL_KEY,
        header: () => (
          <div className="w-sm text-center th-sticky sticky-col detail">
            {f({ id: 'table.detailBtn' })}
          </div>
        ),
        cell: (info) =>
          info.row.original ? (
            <DetailAction
              id={info.row.original.id}
              tableName={TABLE_SUPPLIER}
              customerId={info.row.original.customerId.id}
              onClickCallback={handleAction}
            />
          ) : null,
        size: 60, // Adjusted for consistency with header styling
        enablePinning: true,
      },
      {
        accessorKey: SUPPLIER_DELETED_AT_KEY,
        header: () => (
          <div className="w-sm text-center th-sticky sticky-col detail">
            {f({ id: 'table.deleteBtn' })}
          </div>
        ),
        cell: (info) =>
          info.row.original ? (
            <DeleteAction
              id={info.row.original.id}
              tableName={TABLE_SUPPLIER}
              customerId={info.row.original.customerId.id}
              onClickCallback={handleRemove}
              showConfirmDialog={true}
            />
          ) : null,
        size: 80, // Adjusted for consistency with header styling
        enablePinning: true,
      },
    ],
    [f, handleRemove]
  );

  const columnOrder = React.useMemo(() => {
    const order = columnsTable.map((item) => item.key);
    // add the detail and delete columns to the end
    order.push(SUPPLIER_DETAIL_KEY, SUPPLIER_DELETED_AT_KEY);
    return order;
  }, [columnsTable]);

  const columnVisibility = React.useMemo(() => {
    return columnsTable.reduce<Record<string, boolean>>(
      (acc, column) => {
        acc[column.key] = !column.hidden;
        return acc;
      },
      {} as Record<string, boolean>
    );
  }, [columnsTable]);

  return {
    columns,
    columnOrder,
    columnVisibility,
  };
}
